/**
 * Terms of use (§15).
 *
 * The published counterpart of `docs/terms-of-use.md`. The two are the same
 * document and must be changed together; a substantive change also means bumping
 * `POLICY_VERSION.terms` in `src/config/policies.js` *and* the server's copy in
 * `functions/src/shared/config.js`, which asks every signed-in account to re-accept.
 *
 * Clauses are real ordered lists rather than numbers typed into the prose, so the
 * numbering a reader cites matches the numbering assistive technology announces.
 * `src/ui/legal.js` renders the document; the privacy policy is built the same way.
 *
 * The framing in §14 is load-bearing and survives any edit: KinCode is not an
 * emergency service, does not replace 999 or 112, and promises no availability.
 */

import { el } from "../ui/dom.js";
import {
  bullets,
  clauses,
  prose,
  renderLegalDocument,
  subheading,
  supportLink,
} from "../ui/legal.js";

/** @type {import("../app/router.js").PageRenderer} */
export default function renderTerms() {
  return renderLegalDocument({
    title: "Terms of Use",
    updated: "5 August 2026",
    slug: "terms",
    sections: termsSections(),
  });
}

/**
 * A defined term: the bold word, then its definition.
 *
 * @param {string} term
 * @param {(Node | string)[]} definition
 * @returns {(Node | string)[]}
 */
function defined(term, definition) {
  return [el("strong", {}, [term]), ...definition];
}

/** @returns {HTMLElement} The KinCode postal address, as used for cancellations. */
function postalAddress() {
  return el("p", { class: "kin-prose" }, [
    "KinCode",
    el("br"),
    "DRMSITE LTD",
    el("br"),
    "40 Elm Close",
    el("br"),
    "Witchford",
    el("br"),
    "Ely",
    el("br"),
    "CB6 2JH",
    el("br"),
    "United Kingdom",
  ]);
}

/**
 * The terms themselves, section by section.
 *
 * Built fresh on each render rather than held as module state, so a second render
 * cannot move nodes out of the first one's fragment.
 *
 * @returns {import("../ui/legal.js").LegalSection[]}
 */
function termsSections() {
  return [
    {
      id: "short-version",
      title: "The short version",
      content: [
        prose([
          "KinCode is a contact-recovery service. It lets an adult account holder save a small number of important telephone contacts behind a system-generated four-word phrase and a separate six-digit PIN.",
        ]),
        prose([
          "Only the adult setting up and managing a recovery profile needs an account. A person retrieving contacts does not need to register or tell KinCode who they are.",
        ]),
        prose(["Please remember:"]),
        bullets([
          "KinCode is not an emergency service, care alarm, medical alert service or safeguarding service.",
          "KinCode does not replace 999, 112 or any other emergency arrangement.",
          "KinCode requires a working internet connection and a compatible device.",
          "We cannot guarantee that KinCode will always be available.",
          "Account holders are responsible for keeping contact details accurate and for having an appropriate reason to store them.",
          "Anyone who has both the phrase and PIN can retrieve the contacts associated with that profile.",
          "A card printed from KinCode carries the phrase and never the PIN.",
          "The phrase and PIN should be kept separately.",
          "KinCode has a free plan and paid Subscriptions. A Subscription renews automatically unless cancelled.",
          "KinCode does not sell, print or post cards itself. It may show a link to a third-party printing company for those who want cards printed professionally; any order placed there is between you and that company.",
        ]),
        prose([
          "You should maintain another reasonable way of obtaining help. Do not rely on KinCode as your only emergency arrangement.",
        ]),
        prose([
          "Please read these Terms carefully. By creating an account, purchasing a subscription or otherwise using KinCode, you agree to these Terms. If you do not agree, you must not use KinCode.",
        ]),
      ],
    },
    {
      id: "definitions",
      title: "1. Definitions",
      content: [
        prose(["In these Terms:"]),
        clauses([
          defined("Account", [
            " means an account registered by an Account Holder to manage KinCode recovery profiles.",
          ]),
          defined("Account Holder", [" means an adult who creates and manages an Account."]),
          defined("Contact", [
            " means a first name or simple identifying label and telephone number saved within a Recovery Profile.",
          ]),
          defined("Credentials", [
            " means the system-generated four-word phrase and separate six-digit PIN associated with a Recovery Profile.",
          ]),
          [
            el("strong", {}, ["KinCode"]),
            ", ",
            el("strong", {}, ["we"]),
            ", ",
            el("strong", {}, ["us"]),
            " or ",
            el("strong", {}, ["our"]),
            " means DRMSITE LTD, incorporated and registered in England and Wales with company number 17151917, whose registered office is at 40 Elm Close, Witchford, Ely, CB6 2JH.",
          ],
          defined("Payment Provider", [
            " means a third-party provider used to process subscription or purchase payments.",
          ]),
          defined("Platform", [
            " means the KinCode website, application, public retrieval page and related services.",
          ]),
          defined("Printed Card", [
            " means a card printed by a User from KinCode, showing a Recovery Profile’s phrase, the KinCode web address and brief instructions.",
          ]),
          defined("Recovery Profile", [
            " means an encrypted set of Contacts associated with a particular four-word phrase and PIN.",
          ]),
          defined("Retrieval User", [
            " means a person who uses Credentials to retrieve Contacts, whether or not that person is also the Account Holder.",
          ]),
          defined("Subscription", [
            " means a paid, recurring right to use the relevant KinCode features for the billing period shown at checkout.",
          ]),
          defined("Terms", [" means these terms of use."]),
          [
            el("strong", {}, ["User"]),
            " or ",
            el("strong", {}, ["you"]),
            " means an Account Holder, Retrieval User or other person accessing the Platform, as the context requires.",
          ],
        ]),
      ],
    },
    {
      id: "about-kincode",
      title: "2. About KinCode",
      content: [
        clauses([
          "KinCode provides a way to recover a small number of telephone contacts when the phone or device that would normally hold them is unavailable.",
          "An Account Holder can create a Recovery Profile containing up to the number of Contacts permitted by their Subscription.",
          "KinCode generates a four-word phrase and a separate six-digit PIN for each Recovery Profile.",
          "A Retrieval User who enters both Credentials correctly can view the first names or labels and telephone numbers saved in that Recovery Profile.",
          "KinCode does not independently identify the Retrieval User. Possession of the correct phrase and PIN is treated as authority to retrieve the associated Contacts.",
          {
            text: "KinCode does not:",
            sub: [
              "contact anyone on a User’s behalf;",
              "monitor a User’s location;",
              "detect falls, danger, illness or distress;",
              "alert an Account Holder when a Recovery Profile is used;",
              "provide a staffed helpline;",
              "verify that a retrieved telephone number is current or answered;",
              "guarantee that a Contact will be willing or able to help;",
              "replace emergency services, safeguarding arrangements or professional care.",
            ],
          },
          "Nothing in these Terms creates an agency, partnership, joint venture, employment or care relationship between KinCode and any User, or between an Account Holder, Retrieval User and Contact.",
        ]),
      ],
    },
    {
      id: "emergencies",
      title: "3. Emergencies and appropriate reliance",
      content: [
        clauses([
          "KinCode is a contact-recovery utility. It is not an emergency service.",
          "In an immediate emergency, call 999 or 112 or seek appropriate help without waiting to access KinCode.",
          {
            text: "You must not rely on KinCode as:",
            sub: [
              "your only means of contacting emergency assistance;",
              "a substitute for a care alarm, medical alert device or safeguarding plan;",
              "proof that a person is safe;",
              "a guarantee that someone can be reached;",
              "a substitute for professional medical, legal, safeguarding or other advice.",
            ],
          },
          {
            text: "KinCode requires:",
            sub: [
              "an internet-connected device;",
              "access to the KinCode website;",
              "the correct phrase;",
              "the correct PIN.",
            ],
          },
          "KinCode may not be suitable where the intended Retrieval User cannot reliably retain, locate or use either the phrase or the PIN.",
          "Account Holders should maintain a reasonable alternative arrangement appropriate to their circumstances.",
        ]),
      ],
    },
    {
      id: "eligibility",
      title: "4. Eligibility and accounts",
      content: [
        clauses([
          "You must be at least 18 years old to create an Account, purchase a Subscription or manage a Recovery Profile.",
          {
            text: "By registering an Account, you confirm that:",
            sub: [
              "you are at least 18;",
              "you are acting as a consumer, wholly or mainly outside your trade, business, craft or profession;",
              "the information you provide is accurate and not misleading;",
              "you will keep your Account information current.",
            ],
          },
          "A child or other person under 18 may use the public retrieval page where they have been given valid Credentials by an appropriate adult. They do not need to create an Account.",
          {
            text: "You are responsible for:",
            sub: [
              "maintaining the confidentiality of your Account login details;",
              "protecting access to the email account associated with KinCode;",
              "all activity carried out through your Account;",
              "notifying us promptly if you believe your Account has been compromised.",
            ],
          },
          "You must not share your Account password or permit another person to impersonate you.",
          "Where KinCode later supports additional adult administrators, each administrator should use their own authorised access rather than sharing login credentials.",
          "We may require you to verify your email address before allowing you to create or manage Recovery Profiles.",
        ]),
      ],
    },
    {
      id: "creating-a-profile",
      title: "5. Creating a Recovery Profile",
      content: [
        clauses([
          {
            text: "You may create a Recovery Profile:",
            sub: [
              "for your own use;",
              "for a child for whom you have appropriate parental or caring responsibility;",
              "for another adult who has agreed to the arrangement;",
              "for another person where you otherwise have appropriate legal authority or responsibility.",
            ],
          },
          "You must not create a Recovery Profile for another competent adult without their knowledge and agreement.",
          {
            text: "When adding Contacts, you must:",
            sub: [
              "have an appropriate reason to store each person’s details;",
              "add only people who may reasonably expect to be contacted;",
              "tell adult Contacts that their details have been added where appropriate;",
              "keep names, labels and telephone numbers accurate;",
              "remove Contacts that are no longer appropriate or current.",
            ],
          },
          {
            text: "A Recovery Profile is intended to contain only:",
            sub: [
              "a short profile label, where offered;",
              "a Contact’s first name or simple identifying label;",
              "the Contact’s telephone number;",
              "the preferred order in which Contacts should be tried.",
            ],
          },
          {
            text: "You must not use Recovery Profile fields to store:",
            sub: [
              "passwords or security answers;",
              "bank or payment information;",
              "addresses;",
              "detailed medical information;",
              "precise location information;",
              "unnecessary information about children;",
              "unlawful, threatening, abusive or defamatory material;",
              "information that infringes another person’s privacy or rights.",
            ],
          },
          {
            text: "KinCode does not independently verify:",
            sub: [
              "whether a telephone number belongs to the named Contact;",
              "whether a Contact has agreed to be included;",
              "whether a Contact remains willing or able to help;",
              "the relationship between an Account Holder, Retrieval User and Contact;",
              "whether the Account Holder has legal authority to act for another person.",
            ],
          },
          "You are responsible for reviewing Recovery Profiles regularly and correcting outdated information.",
        ]),
      ],
    },
    {
      id: "credentials",
      title: "6. Credentials and contact retrieval",
      content: [
        clauses([
          "KinCode generates the phrase and PIN. Account Holders and Retrieval Users do not choose them.",
          "The phrase consists of words selected from KinCode’s controlled word list.",
          "The PIN consists of six digits and may begin with zero.",
          "The phrase and PIN form separate parts of the Credentials. Both are required to retrieve Contacts.",
          {
            text: "You should:",
            sub: [
              "practise using the Credentials where appropriate;",
              "keep the PIN separate from any written or printed copy of the phrase;",
              "avoid sending both parts together in the same message;",
              "avoid placing both parts in the same wallet, purse, bag or document;",
              "reset the Credentials if you believe both parts have been disclosed.",
            ],
          },
          "Anyone who has both the phrase and PIN may be able to retrieve the associated Contacts. KinCode does not perform an additional identity check.",
          {
            text: "If you share Credentials, you are responsible for deciding:",
            sub: [
              "who should receive them;",
              "how they should be communicated;",
              "whether the recipient can retain and use them appropriately;",
              "when they should be reset.",
            ],
          },
          "KinCode may apply rate limits, delays, security challenges and temporary restrictions to prevent automated guessing or misuse.",
          "Security controls may occasionally delay or reject a genuine request.",
          "We do not provide Account Holders with a log showing whether, when, where or by whom a Recovery Profile was retrieved.",
          {
            text: "When using a borrowed or public device, the Retrieval User should:",
            sub: [
              "hide the Contacts after use;",
              "close the KinCode page;",
              "avoid allowing the browser to save Credentials;",
              "understand that a called number may remain in the device’s telephone, browser or operating-system history.",
            ],
          },
          "KinCode cannot remove a telephone number from the call history or other records created by a device or third-party service.",
        ]),
      ],
    },
    {
      id: "printed-cards",
      title: "7. Printed Cards",
      content: [
        clauses([
          "KinCode does not sell, print, post or deliver cards itself. A Printed Card produced within KinCode is generated by your own browser and printer, and nothing is sent to us in the process.",
          "KinCode may show a link to a third-party printing company that can produce professional cards from the print file you download. Using that link starts a separate transaction between you and that company, under its own terms and privacy policy. KinCode does not send that company any information and is not responsible for its service. Where such a link is a paid referral, it is labelled as one, and we may earn a commission if you order through it.",
          "Printed Cards are optional. A handwritten note containing the phrase can perform the same basic function.",
          {
            text: "A Printed Card contains:",
            sub: [
              "the profile label;",
              "the four-word phrase;",
              "a QR code containing the four-word phrase and the KinCode web address;",
              "the KinCode web address;",
              "brief retrieval instructions.",
            ],
          },
          {
            text: "A Printed Card does not contain:",
            sub: [
              "the PIN;",
              "Contact names or labels;",
              "telephone numbers;",
              "the Account Holder’s email address.",
            ],
          },
          "Scanning the QR code opens the retrieval page with the phrase already filled in. It does not retrieve Contacts on its own: the PIN must still be entered, and it is not on the card.",
          "Anyone who sees the Printed Card can learn the phrase, and a QR code can be read by a camera at a distance or from a photograph. This is why the PIN must be kept separately, and why a Printed Card should be treated as you would treat a spare key.",
          "If you replace a Recovery Profile’s Credentials, a previously printed card for that profile will stop working. This is how a lost card is cancelled, and it can be done from your dashboard at any time.",
          "If a Printed Card is lost, anyone finding it may obtain the phrase. You should replace the phrase for that Recovery Profile, and also the PIN if you believe it may have been disclosed.",
          "If we later offer cards printed and delivered by a print and fulfilment provider, we will publish the additional terms that apply before that service becomes available. A card produced that way would still never carry the PIN, Contact names or telephone numbers.",
        ]),
      ],
    },
    {
      id: "subscriptions",
      title: "8. Subscriptions and payment",
      content: [
        clauses([
          "KinCode offers a free plan with reduced limits, and paid Subscriptions that raise those limits. The plan, its price and its limits are shown before you subscribe.",
          {
            text: "Before purchase, we will show:",
            sub: [
              "the Subscription price;",
              "the billing period;",
              "the features and limits included;",
              "whether the Subscription renews automatically;",
              "any applicable trial or introductory period;",
              "how to cancel.",
            ],
          },
          "Prices are shown in the currency and tax treatment displayed at checkout.",
          "Payments are processed by a Payment Provider. The Payment Provider may apply its own terms and privacy policy to the payment process.",
          "We do not receive or store your complete payment-card details.",
          {
            text: "When you purchase a Subscription:",
            sub: [
              "you authorise the Payment Provider to collect the amount shown at checkout;",
              "the Subscription begins when payment has been accepted and we make the paid service available;",
              "we will provide confirmation in a form that you can save.",
            ],
          },
          "Unless cancelled, the Subscription renews automatically at the end of each billing period using the payment method held by the Payment Provider.",
          "You are responsible for keeping your billing email address and payment method current.",
          "We will provide renewal reminders, cooling-off information and other notices where required by law.",
          "You can cancel through the billing or account area where that facility is available, or by contacting us.",
          {
            text: "Unless applicable law or a notice provided to you says otherwise:",
            sub: [
              "cancellation prevents the next renewal;",
              "your paid features remain available until the end of the current paid period;",
              "cancelling does not normally produce a refund for a partly used billing period.",
            ],
          },
          {
            text: "If payment fails, we or the Payment Provider may:",
            sub: [
              "retry the payment;",
              "ask you to update your payment method;",
              "place the Subscription into a temporary grace period;",
              "restrict the creation or editing of Recovery Profiles;",
              "eventually suspend paid features.",
            ],
          },
          "We will not delete your saved Contacts because a payment fails.",
          "Retrieval is never restricted for non-payment. If a Subscription ends or remains unpaid, the account returns to the free plan: existing Recovery Profiles stay in place and stay retrievable, and the account simply cannot create profiles beyond the free limit until it subscribes again.",
          "This is not a promise of permanent free access to paid features, and it does not prevent us suspending an Account under clause 16 for a reason other than non-payment.",
        ]),
      ],
    },
    {
      id: "cancellation",
      title: "9. Cancellation and refunds",
      content: [
        clauses([
          "If you are a consumer purchasing online, you may have a legal right to change your mind during an initial cooling-off period.",
          {
            text: "You can tell us that you wish to cancel by:",
            sub: [
              "using the cancellation facility in your Account;",
              "emailing us;",
              "sending another clear statement that you wish to cancel;",
              "using the model cancellation form at the end of these Terms.",
            ],
          },
          "Where you ask for KinCode to begin during a statutory cooling-off period, we may ask you to expressly request immediate supply.",
          "If you cancel during a statutory cooling-off period after the service has begun, any refund or deduction will be handled in accordance with applicable consumer law and the information shown at checkout.",
          "Any additional cancellation right following an automatic renewal will be explained in the relevant renewal or cooling-off notice where required.",
          {
            text: "Outside an applicable cooling-off period, payments are not normally refundable merely because:",
            sub: [
              "you did not use KinCode;",
              "you forgot to cancel before a renewal;",
              "the person for whom a Recovery Profile was created no longer needs it;",
              "you lost or forgot the Credentials.",
            ],
          },
          {
            text: "We may nevertheless issue a refund where:",
            sub: [
              "you were charged incorrectly;",
              "a duplicate payment was taken;",
              "the paid service was materially unavailable because of our fault;",
              "we permanently discontinue the paid service during a period for which you have already paid;",
              "applicable law requires a refund;",
              "we decide that a refund is fair in the circumstances.",
            ],
          },
          "A refund does not prevent us from ending or reducing access to the refunded paid service.",
          "Nothing in this section affects rights that cannot lawfully be excluded or restricted.",
        ]),
      ],
    },
    {
      id: "acceptable-use",
      title: "10. Acceptable use",
      content: [
        clauses([
          {
            text: "You must:",
            sub: [
              "comply with applicable law;",
              "use KinCode only for its intended contact-recovery purpose;",
              "respect the privacy and rights of other people;",
              "use only Accounts, Credentials and Recovery Profiles you are authorised to use;",
              "provide accurate information when dealing with us.",
            ],
          },
          {
            text: "You must not:",
            sub: [
              "attempt to guess, discover, test or retrieve another person’s Credentials;",
              "use automated tools to test phrases or PINs;",
              "retrieve Contacts without permission;",
              "create a Recovery Profile for the purpose of monitoring, harassing, coercing or controlling another person;",
              "misuse a retrieved telephone number;",
              "impersonate another person;",
              "provide false or misleading Account, billing or delivery information;",
              "use KinCode for unlawful surveillance;",
              "use KinCode to store unlawful or unnecessary personal information;",
              "attempt to gain unauthorised access to the Platform, its servers, databases, encryption keys or connected systems;",
              "interfere with, disrupt or impair the Platform;",
              "circumvent App Check, rate limits, security challenges or other safeguards;",
              "knowingly introduce viruses, trojans, worms, logic bombs or other malicious or technologically harmful material;",
              "attack the Platform through a denial-of-service or distributed denial-of-service attack;",
              "decompile, disassemble or reverse engineer the Platform except where the law expressly permits it;",
              "reproduce, modify, distribute, sell or commercially exploit the Platform without our written permission;",
              "use bots, scrapers or other automated means to access, copy, monitor or republish the Platform or its data;",
              "use the Platform or its data to develop, train, fine-tune or validate an artificial-intelligence model without our written permission;",
              "conduct security testing without our prior written authorisation;",
              "use the Platform for a commercial, institutional or organisational service unless we have expressly agreed to that use.",
            ],
          },
          {
            text: "The restrictions on automated access do not prevent:",
            sub: [
              "ordinary search-engine indexing of public pages;",
              "assistive or accessibility technologies;",
              "reasonable browser functionality;",
              "security research that we have authorised in writing.",
            ],
          },
          "Unauthorised access to KinCode may also constitute a criminal offence.",
        ]),
      ],
    },
    {
      id: "your-information",
      title: "11. Your information and permissions",
      content: [
        clauses([
          "You remain responsible for the information you add to a Recovery Profile.",
          "You retain any rights you have in your own information.",
          {
            text: "You give us a limited, non-exclusive permission to:",
            sub: [
              "host and store the information;",
              "encrypt and decrypt it;",
              "process it to provide the Account and retrieval functions;",
              "back up, secure and restore the information;",
              "remove it when the Account or Recovery Profile is deleted.",
            ],
          },
          {
            text: "This permission exists only for operating, securing, supporting and improving KinCode. It does not permit us to:",
            sub: [
              "sell Contact information;",
              "use Contacts for advertising;",
              "contact saved Contacts for marketing;",
              "display private Recovery Profile information publicly;",
              "use private Contacts in external promotional material.",
            ],
          },
          "You confirm that our processing of the information in accordance with these Terms and the Privacy Policy will not infringe another person’s rights.",
        ]),
      ],
    },
    {
      id: "intellectual-property",
      title: "12. Intellectual property",
      content: [
        clauses([
          {
            text: "Except for information provided by Users and third-party materials, KinCode owns or licenses the intellectual-property rights in:",
            sub: [
              "the Platform;",
              "its software;",
              "its design and layout;",
              "its text and graphics;",
              "the KinCode name and branding;",
              "its databases and controlled word lists;",
              "its Printed Card design.",
            ],
          },
          "We grant you a limited, non-exclusive, personal, revocable and non-transferable licence to use KinCode for domestic, private and non-commercial purposes in accordance with these Terms.",
          "You may print, keep and reprint a Printed Card for your own Recovery Profile.",
          "You must not sell, license, publish or reuse KinCode’s card design or branding for another service without written permission.",
          "No rights are transferred to you other than those expressly granted in these Terms.",
        ]),
      ],
    },
    {
      id: "data-protection",
      title: "13. Data protection",
      content: [
        clauses([
          [
            "We process personal data in accordance with the KinCode ",
            el("a", { href: "/privacy", dataset: { link: "" } }, ["Privacy Policy"]),
            ".",
          ],
          {
            text: "The Privacy Policy explains:",
            sub: [
              "what information KinCode collects;",
              "why it is used;",
              "how Recovery Profile data is protected;",
              "how the Payment Provider processes information;",
              "how long information is retained;",
              "the rights available under data-protection law.",
            ],
          },
          "You should read the Privacy Policy before creating a Recovery Profile for another person or adding a Contact.",
        ]),
      ],
    },
    {
      id: "third-party-services",
      title: "14. Third-party services",
      content: [
        clauses([
          {
            text: "KinCode relies on third-party services for matters including:",
            sub: [
              "hosting and cloud infrastructure;",
              "authentication;",
              "security and bot protection;",
              "payment processing;",
              "email delivery.",
            ],
          },
          "Third-party services may have their own terms and privacy policies.",
          "We are not responsible for a third-party service’s separate website, application or acts that are outside our reasonable control.",
          "This does not remove any responsibility KinCode has for selecting and managing service providers appropriately or any liability that cannot legally be excluded.",
          "A link to a third-party service does not necessarily mean that we endorse every product, statement or service it provides.",
        ]),
      ],
    },
    {
      id: "availability",
      title: "15. Platform operation and availability",
      content: [
        clauses([
          "We will provide KinCode with reasonable care and skill.",
          {
            text: "KinCode is provided on an “as available” basis. We do not promise that it will always be:",
            sub: [
              "uninterrupted;",
              "available from every location or network;",
              "compatible with every device or browser;",
              "free from faults;",
              "immune from malicious attacks;",
              "able to retrieve every Recovery Profile immediately.",
            ],
          },
          {
            text: "Availability may be affected by:",
            sub: [
              "planned maintenance;",
              "emergency maintenance;",
              "internet or telecommunications failures;",
              "cloud-provider or Payment Provider outages;",
              "security incidents;",
              "unusually high demand;",
              "events outside our reasonable control.",
            ],
          },
          {
            text: "We may change KinCode to:",
            sub: [
              "improve security, accessibility or performance;",
              "add, remove or alter features;",
              "comply with law or regulatory requirements;",
              "respond to misuse or technical risk;",
              "change a service provider.",
            ],
          },
          "Where a change is expected to materially and detrimentally affect a paid feature, we will give reasonable notice where practicable.",
          {
            text: "We may make an immediate change without advance notice where necessary to:",
            sub: [
              "protect Users or personal data;",
              "respond to an emergency or security incident;",
              "comply with law;",
              "address an event outside our control;",
              "prevent significant harm.",
            ],
          },
          "If we permanently withdraw KinCode during a paid Subscription period for reasons unrelated to your breach, we will provide an appropriate refund for any paid service that will not be supplied.",
          "You should keep an independent and current record of important telephone numbers. KinCode should not be your only copy.",
        ]),
      ],
    },
    {
      id: "suspension",
      title: "16. Suspension and termination",
      content: [
        clauses([
          {
            text: "We may suspend or restrict an Account, Recovery Profile, Subscription or other access where we reasonably believe that:",
            sub: [
              "these Terms have been breached;",
              "an Account or Profile has been compromised;",
              "Credentials are being attacked or misused;",
              "false or fraudulent payment information has been used;",
              "payment for a Subscription remains outstanding, subject to clause 8, which limits what non-payment means for existing Recovery Profiles;",
              "the Platform is being used unlawfully;",
              "continued access presents a risk to another person, KinCode or its systems;",
              "suspension is required by law or a service provider.",
            ],
          },
          "We may act immediately where necessary to protect people, personal data or the security of the Platform.",
          {
            text: "Where appropriate and reasonably possible, we will:",
            sub: [
              "explain the reason for a suspension;",
              "provide an opportunity to correct the problem;",
              "act proportionately;",
              "restore access when the issue has been resolved.",
            ],
          },
          "We are not required to disclose technical security information where doing so could assist an attack or put another person at risk.",
          "You may stop using KinCode at any time.",
          "You may delete individual Recovery Profiles or request deletion of your Account using the available Account controls or by contacting us.",
          "Deleting a Recovery Profile will invalidate its Credentials and Printed Cards.",
          "Requesting deletion of your Account starts a 30-day recovery period. Retrieval stops and any Subscription is cancelled straight away, but the Account and its Recovery Profiles are not erased until the period ends, so an accidental deletion can be undone. You can cancel the request at any time during that period, after which erasure is permanent and cannot be reversed.",
          "Deleting your Account does not automatically remove billing records that we or a Payment Provider must retain for legal, tax, fraud-prevention or accounting purposes.",
          "Requesting deletion of your Account also cancels any Subscription it holds. Cancelling a Subscription on its own does not delete your Account: if you want both, request deletion.",
          "Clauses that by their nature should continue after termination—including intellectual property, accrued payment obligations, liability, data retention and governing law—will remain effective.",
        ]),
      ],
    },
    {
      id: "our-responsibility",
      title: "17. Our responsibility to you",
      content: [
        clauses([
          "We are responsible for foreseeable loss or damage caused by our breach of these Terms or our failure to use reasonable care and skill.",
          "Loss or damage is foreseeable if it was an obvious consequence of our breach or if you and we contemplated it when the contract was made.",
          {
            text: "We are not responsible for loss or damage caused by:",
            sub: [
              "inaccurate or outdated Contact information supplied by an Account Holder;",
              "a Contact not answering or being unable or unwilling to help;",
              "loss, disclosure or misuse of Credentials where this was not caused by our breach;",
              "keeping the phrase and PIN together;",
              "a lost or copied Printed Card;",
              "a User entering the wrong Credentials;",
              "lack of internet, mobile signal, power or a compatible device;",
              "records retained in a borrowed device’s call history;",
              "use of KinCode for a purpose it was not designed to serve;",
              "failure to maintain a reasonable alternative arrangement;",
              "an event outside our reasonable control where we have taken reasonable steps to reduce its effects;",
              "a third-party service acting outside our reasonable control.",
            ],
          },
          {
            text: "We do not guarantee that:",
            sub: [
              "the Platform will always be available;",
              "every attempted retrieval will succeed;",
              "every saved number is valid;",
              "a Contact will answer;",
              "use of KinCode will prevent harm, loss, distress or inconvenience.",
            ],
          },
          "We supply KinCode only for domestic and private use.",
          {
            text: "If you use KinCode for a commercial or business purpose without our written agreement, we are not responsible for:",
            sub: [
              "loss of profit;",
              "loss of business;",
              "business interruption;",
              "loss of business opportunity.",
            ],
          },
          {
            text: "Nothing in these Terms excludes or limits our liability for:",
            sub: [
              "death or personal injury caused by our negligence;",
              "fraud or fraudulent misrepresentation;",
              "breach of rights that cannot lawfully be excluded;",
              "any other liability that cannot legally be limited or excluded.",
            ],
          },
          "Nothing in these Terms affects your statutory consumer rights.",
        ]),
      ],
    },
    {
      id: "changes",
      title: "18. Changes to prices and these Terms",
      content: [
        clauses([
          "We may change Subscription prices.",
          "A price change will not normally affect a billing period that has already been paid for.",
          "We will give reasonable advance notice of a price increase before it applies to a future renewal.",
          "If you do not accept a price change, you can cancel before the affected renewal.",
          {
            text: "We may amend these Terms to reflect:",
            sub: [
              "changes to KinCode;",
              "changes to payment arrangements;",
              "security improvements;",
              "changes in law or regulation;",
              "changes to our business or service providers.",
            ],
          },
          "Where a change is material, we will give reasonable notice through the Platform, your Account or email where practicable.",
          "Updated Terms take effect on the date stated in the notice or, if no date is stated, when they are published.",
          "If you do not agree to updated Terms, you must stop using KinCode and cancel your Subscription before the changes take effect.",
          "Continued use after the updated Terms take effect constitutes acceptance, except where applicable law requires another form of agreement.",
        ]),
      ],
    },
    {
      id: "general",
      title: "19. General terms",
      content: [
        clauses([
          "Neither you nor we are responsible for a failure or delay caused by circumstances outside reasonable control, provided reasonable steps are taken to minimise the effects where possible.",
          "If a court or other authority finds any part of these Terms unlawful or unenforceable, the remaining parts will continue to apply.",
          "These Terms are between you and KinCode. No other person has a right to enforce them.",
          "We may transfer our rights and obligations under these Terms to another organisation. We will tell you in writing if this happens and will ensure that the transfer does not reduce your legal rights.",
          "You may not transfer your Account, Subscription or rights under these Terms to another person without our written permission.",
          "If we delay enforcing a provision, that does not prevent us enforcing it later.",
          "A heading is provided for convenience and does not affect the interpretation of these Terms.",
          "These Terms, the Privacy Policy and the information shown at checkout form the agreement between you and us for the relevant service.",
          "If information shown at checkout conflicts with these Terms in relation to a particular price, billing period or product, the specific checkout information will take priority for that purchase.",
        ]),
      ],
    },
    {
      id: "contact",
      title: "20. Contact",
      content: [
        prose(["Questions, complaints and cancellation requests can be sent to:"]),
        prose([el("strong", {}, ["Email:"]), " ", supportLink()]),
        prose([el("strong", {}, ["Post:"])]),
        postalAddress(),
        prose([
          "Please do not include a PIN or unnecessary Contact information in an email or letter.",
        ]),
      ],
    },
    {
      id: "governing-law",
      title: "21. Governing law",
      content: [
        clauses([
          "These Terms, their subject matter and their formation are governed by English law.",
          "The courts of England and Wales will have jurisdiction.",
          "If you live in Scotland, you may also bring proceedings in Scotland.",
          "If you live in Northern Ireland, you may also bring proceedings in Northern Ireland.",
        ]),
      ],
    },
    {
      id: "cancellation-form",
      title: "Model cancellation form",
      content: [
        prose([
          "Complete and return this form only if you wish to cancel a contract with KinCode. You do not have to use this form; any clear statement that you wish to cancel is sufficient.",
        ]),
        subheading("To"),
        postalAddress(),
        prose([el("strong", {}, ["Email:"]), " ", supportLink()]),
        subheading("What to include"),
        prose(["I give notice that I wish to cancel my contract for the following service:"]),
        el("dl", { class: "kin-details" }, [
          el("dt", {}, ["Service or Subscription"]),
          el("dd", {}, ["[Enter details]"]),
          el("dt", {}, ["Order or account email address"]),
          el("dd", {}, ["[Enter email address]"]),
          el("dt", {}, ["Ordered on"]),
          el("dd", {}, ["[Enter date]"]),
          el("dt", {}, ["Name"]),
          el("dd", {}, ["[Enter name]"]),
          el("dt", {}, ["Address"]),
          el("dd", {}, ["[Enter address, where needed to identify the order]"]),
          el("dt", {}, ["Signature"]),
          el("dd", {}, ["[Only required if this form is sent on paper]"]),
          el("dt", {}, ["Date"]),
          el("dd", {}, ["[Enter date]"]),
        ]),
      ],
    },
  ];
}
